import React from 'react';
import Button from '../Button/Button';
import StressLevelChart from '../Charts/StressLevelChart';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import { selectStressLevelLabels } from '@/redux/selectors/stressLevelSelector';
import CloseButton from '../Elements/CloseButton';
import { T, useTranslate } from '@tolgee/react';

interface StressTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentQuestion: number;
  totalQuestions: number;
  questionText: string;
  onAnswer: (answer: number) => void;
  onPrevious: () => void;
  isCompleted?: boolean;
  score?: number;
}

const StressTestModal: React.FC<StressTestModalProps> = ({
  isOpen,
  onClose,
  currentQuestion,
  totalQuestions,
  questionText,
  onAnswer,
  onPrevious,
  isCompleted,
  score,
}) => {
  const router = useRouter();
  const labels = useSelector(selectStressLevelLabels);
  const { t } = useTranslate();

  const handleButtonClick = () => {
    if (score === undefined) return;

    // Determine the score range
    let scoreRange = '';
    if (score < 7) {
      scoreRange = '0-7';
    } else if (score < 14) {
      scoreRange = '8-14';
    } else {
      scoreRange = '15-21';
    }

    onClose();
    let link;
    switch (scoreRange) {
      case '0-7':
        link = '/?category=personal-development';
        break;
      case '8-14':
        link = '/?category=habits';
        break;
      case '15-21':
        link = '/?category=stress';
        break;
      default:
        link = '/';
    }

    router.push(link);
  };

  const lastLabelDate = labels[0] !== '0' ? dayjs(labels[labels.length - 1]) : null;
  const formattedDate = lastLabelDate ? lastLabelDate.format('MMM DD') : '';

  const getStressLevel = (score: number) => {
    if (score < 7) {
      return {
        title: t('stresstest.survey-result.content.title.0-7-points.label'),
        headline:
          t('stresstest.result_overview.stresslevel_explanation.0-7-points.label') +
          ' ' +
          t('stresstest.result.points_count.label') +
          ` ${score}`,
        description: t('stresstest.result.content.text.0-7-points.label'),
        level: 'niedrig',
      };
    }
    if (score < 14) {
      return {
        title: t('stresstest.survey-result.content.title.8-14-points.label'),
        headline:
          t('stresstest.result_overview.stresslevel_explanation.8-15-points.label') +
          ' ' +
          t('stresstest.result.points_count.label') +
          ` ${score}`,
        description: t('stresstest.survey-result.content.text.8-14-points.label'),
        level: 'moderat',
      };
    }
    return {
      title: t('stresstest.survey-result.content.title.15-21-points.label'),
      headline:
        t('stresstest.result_overview.stresslevel_explanation.15-21-points.label') +
        ' ' +
        t('stresstest.result.points_count.label') +
        ` ${score}`,
      description: t('stresstest.survey-result.content.text.15-21-points.label'),
      level: 'hoch',
    };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-2xl shadow-lg w-[650px] relative overflow-hidden">
        <div className="relative rounded-t-large bg-gradient-to-t from-black via-black to-main ">
          <div className="relative top-4 left-0 py-2 px-5 rounded-lg flex justify-between w-full mb-16">
            <p className="text-white text-base">
              <T>stresstest.survey.title.label</T>
            </p>
            <CloseButton onClose={onClose} />
          </div>
          <StressLevelChart />
          <div className="text-right text-base opacity-50 text-white p-5">
            {lastLabelDate && formattedDate}
          </div>
        </div>
        <div className="p-4">
          {isCompleted ? (
            <div className="p-4">
              {score !== undefined &&
                (() => {
                  const { title, headline, description } = getStressLevel(score);
                  return (
                    <div>
                      <p className="text-4xl mb-2.5">{title}</p>
                      <p className="text-lg text-primary tracking-tightest mb-2.5">{headline}</p>
                      <p className="text-base mb-2.5 font-poppins">{description}</p>
                    </div>
                  );
                })()}
              <div className="grid grid-cols-2 gap-4 mt-12">
                <div
                  className="col-span-1 text-base flex items-center underline cursor-pointer"
                  onClick={onClose}
                >
                  <T>stresstest.result.button.close.label</T>
                </div>
                <div className="col-span-1">
                  <Button variant="primary" onClick={handleButtonClick}>
                    <T>stresstest.result.button.show_recommendation.label</T>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between w-full mb-5">
                <div className="text-sm uppercase">
                  {`${currentQuestion}/${totalQuestions}`} <T>stresstest.question-count.label</T>
                </div>
                <div className="text-sm cursor-pointer" onClick={onPrevious}>
                  <T>stresstest.survey.back.label</T>
                </div>
              </div>
              <p className="text-lg mb-28">
                <T>{questionText}</T>
              </p>
              <div className="flex justify-between gap-4">
                <Button variant="secondary" buttonBorderColor="black" onClick={() => onAnswer(1)}>
                  <T>stresstest.button.agree.label</T>
                </Button>
                <Button variant="secondary" buttonBorderColor="black" onClick={() => onAnswer(0)}>
                  <T>stresstest.button.disagree.label</T>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StressTestModal;
